/**
 * Risorse italiane, assemblate per area.
 *
 * L'italiano è la **fonte**: i testi qui dentro sono quelli scritti secondo la
 * skill `testi-ui-italiano`, e le altre lingue sono traduzioni che ne
 * rispettano i principi, non calchi parola per parola.
 *
 * Il tipo `Translations` è ricavato da questo oggetto: una lingua nuova si
 * dichiara `Translations` e il compilatore segnala da solo ogni chiave mancante
 * o di troppo, prima ancora che giri il test di completezza.
 *
 * Le aree stanno in file separati per il limite di `CLAUDE.md` §3.3: un unico
 * dizionario documentato chiave per chiave supererebbe le 500 righe.
 */

import { activity } from './activity';
import { admin } from './admin';
import { agents } from './agents';
import { app } from './app';
import { artifacts } from './artifacts';
import { auth } from './auth';
import { billing } from './billing';
import { common } from './common';
import { conversation } from './conversation';
import { egress } from './egress';
import { environments } from './environments';
import { google } from './google';
import { guided } from './guided';
import { deadlines } from './deadlines';
import { mobileRelease } from './mobileRelease';
import { projectProfile } from './projectProfile';
import { projectTemplate } from './projectTemplate';
import { requirements } from './requirements';
import { siteMonitoring } from './siteMonitoring';
import { giftCodes } from './giftCodes';
import { consumption } from './consumption';
import { priceList } from './priceList';
import { devhub } from './devhub';
import { githubManaged } from './githubManaged';
import { googleManaged } from './googleManaged';
import { help } from './help';
import { history } from './history';
import { members } from './members';
import { monitoring } from './monitoring';
import { permissions } from './permissions';
import { overview } from './overview';
import { profile } from './profile';
import { projects } from './projects';
import { setAgents, setSettings, sets } from './sets';
import { setProjects } from './setProjects';
import { settings } from './settings';
import { storage } from './storage';
import { setup } from './setup';
import { shell } from './shell';
import { tasks } from './tasks';
import { templates } from './templates';
import { tools } from './tools';
import { usage } from './usage';
import { users } from './users';
import { voice } from './voice';
import { work } from './work';

/** Dizionario italiano completo, raggruppato per area. */
export const it = {
  /** Testi condivisi da più aree. */
  common,
  /** Guscio dell'applicazione: marchio, menu, intestazione, 404. */
  app,
  /** Barra in alto, menu utente, pagine provvisorie del guscio (ADR 0030). */
  shell,
  /** Profilo e sicurezza (ADR 0030). */
  profile,
  /** Accesso e verifica in due passaggi. */
  auth,
  /** Progetti, dipendenze, accessi. */
  projects,
  /** Stati e agenti dei set, catalogo dei ruoli. */
  agents,
  /** Set di agenti: elenco, pagina, runtime, credenziali. */
  sets,
  /** Procedura di creazione di un set. */
  setup,
  /** Conversazione con il relay. */
  conversation,
  /** Attività degli agenti e dei subagenti (ADR 0017). */
  activity,
  /** Siti dei progetti da vedere e annotare (ADR 0018 §6). */
  artifacts,
  /** Progetti dentro un set e importazione da cartella. */
  setProjects,
  /** Scheda «Agenti» di un set. */
  setAgents,
  /** Scheda «Impostazioni» di un set. */
  setSettings,
  /** Strumenti: connessioni a GitHub e collegamenti ai progetti. */
  tools,
  /** Uscita verso internet: domini dei siti e release di GitHub (ADR 0039). */
  egress,
  /** Google come strumento: collegamento, pagina di gestione, collegamenti (ADR 0011). */
  google,
  /** Account Google di institor, progetti gestiti, migrazione, consumi (ADR 0012). */
  githubManaged,
  googleManaged,
  /** Elenco degli utenti della piattaforma. */
  users,
  /** Template di sito pubblicati in anteprima. */
  templates,
  /** Monitoraggio: Grafana, Prometheus e allarmi su Telegram. */
  monitoring,
  /** Piano e consumo di token sulla credenziale di institor (ADR 0023 §4). */
  usage,
  /** Agente «voce» del set (ADR 0028). */
  voice,
  /** Task manager del set (ADR 0029). */
  tasks,
  /** Abbonamento, ricarica dei minuti di voce e pagamenti con Stripe (ADR 0027). */
  billing,
  /** Panoramica: da fare, metriche, consumo per giorno (ADR 0030 tappa 6). */
  overview,
  /** Cronologia unica (ADR 0030 tappa 8). */
  history,
  /** Amministrazione: schede e pagina Utenti (ADR 0030 tappa 7). */
  admin,
  /** Impostazioni di questo browser, notifiche, privacy (ADR 0030 tappa 7). */
  settings,
  /** Membri e inviti dei set (ADR 0032). */
  members,
  /** Task di tutti i set, calendario, consumo per progetto (ADR 0032). */
  work,
  /** Aiuto, presentazione iniziale, novità e richieste di aiuto (ADR 0032). */
  help,
  /** Incarichi guidati all'Institore e risultati dei messaggi (ADR 0035). */
  guided,
  /** Pagina dei permessi del set (ADR 0041). */
  permissions,
  /** Codici regalo e inviti dei creator (ADR 0043). */
  giftCodes,
  priceList,
  devhub,
  consumption,
  deadlines,
  projectTemplate,
  mobileRelease,
  /** Scheda tecnica del progetto e modifiche richieste (ADR 0048). */
  projectProfile,
  /** Spazio dei siti ospitati e crediti (ADR 0046). */
  storage,
  /** Ambienti demo, produzione e test dei siti ospitati (ADR 0050). */
  environments,
  /** Requisiti funzionali dei progetti (ADR 0050 §6). */
  requirements,
  /** Monitoraggio del sito di un ambiente (ADR 0056). */
  siteMonitoring,
};

/** Forma del dizionario: ogni lingua deve avere esattamente queste chiavi. */
export type Translations = typeof it;
